'use client';

/**
 * P2PTransferSheet - Internal P2P Transfer (Fullscreen)
 * 
 * Allows users to send funds to other Supabank users by wallet ID.
 * Uses Sheet component for mobile-first fullscreen UX.
 * 
 * Mobile (<1024px): Bottom sheet, h=95vh
 * Desktop (≥1024px): Right sheet, w=28rem
 * 
 * @see PRD-P2P-001
 */

import { Button } from '@/components/ui/button';
import { CurrencyIcon } from '@/components/ui/CurrencyIcon';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { InlineLoadingIndicator } from '@/components/ui/inline-loading';
import { useEffect, useState } from 'react';
import { toast } from '@/hooks/use-toast';
import { BridgeWallet, useWalletBalances, useWalletTransfer } from './hooks/useWallet';

interface P2PTransferSheetProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function P2PTransferSheet({ open, onClose, onSuccess }: P2PTransferSheetProps) {
  const isMobile = useIsMobile();

  // Form state
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [recipientId, setRecipientId] = useState('');
  const [memo, setMemo] = useState('');
  const [sourceWalletId, setSourceWalletId] = useState('');

  // Data
  const { data: wallets, isLoading: isLoadingWallets } = useWalletBalances();
  const transferMutation = useWalletTransfer();

  // Auto-select first wallet when data loads
  useEffect(() => {
    if (wallets && wallets.length > 0 && !sourceWalletId) {
      setSourceWalletId(wallets[0].id);
    }
  }, [wallets, sourceWalletId]);

  // Reset form when sheet closes
  useEffect(() => {
    if (!open) {
      setAmount('');
      setRecipientId('');
      setMemo('');
    }
  }, [open]);

  // Validation
  const isValid = Boolean(
    amount &&
    parseFloat(amount) > 0 &&
    recipientId.trim() &&
    sourceWalletId
  );

  // Get selected wallet balance for display
  const selectedWallet = wallets?.find(w => w.id === sourceWalletId);
  const currencyBalance = selectedWallet?.balances
    .find(b => b.currency.toUpperCase() === currency.toUpperCase());
  const availableBalance = currencyBalance?.balance || '0';

  // Handle transfer submission
  const handleTransfer = async () => {
    if (!isValid) return;

    try {
      await transferMutation.mutateAsync({
        from_wallet_id: sourceWalletId,
        to_wallet_id: recipientId.trim(),
        amount,
        currency,
        memo: memo.trim() || undefined,
      });

      toast.success('Transfer sent successfully!');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Transfer failed');
    }
  };

  // Format balance for display (Bridge API precision: 2 decimals)
  const formatBalance = (balance: string | number): string => {
    const num = typeof balance === 'string' ? parseFloat(balance) : balance;
    return Math.floor(num * 100) / 100 + ''; // Truncate to 2 decimals
  };

  const sheetSide = isMobile ? 'bottom' : 'right';

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        side={sheetSide}
        className={cn(
          'flex flex-col',
          isMobile
            ? 'h-[95vh] max-h-[95vh]'
            : 'w-[28rem] max-w-[28rem]',
          semanticTokens.motion.standard
        )}
      >
        {/* Header */}
        <SheetHeader className="flex-shrink-0 pb-4 border-b">
          <div className="flex items-center gap-3">
            <div className={cn(
              'p-2.5 rounded-xl',
              semanticTokens.bg.muted
            )}>
              <SFIcon icon={sfIcons.send} className="h-5 w-5 text-primary" aria-hidden="true" />
            </div>
            <div>
              <SheetTitle className={semanticTokens.typography.h3}>
                Send Money
              </SheetTitle>
              <SheetDescription className={cn(semanticTokens.typography.caption, 'mt-0.5')}>
                Transfer to another Supabank user. Zero fees.
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Form Content - Scrollable */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden py-6 space-y-6 min-w-0">

          {/* Source Wallet Selection */}
          <div className="space-y-2">
            <Label className={semanticTokens.typography.label}>From Wallet</Label>
            {isLoadingWallets ? (
              <Skeleton className="h-[52px] w-full rounded-xl" />
            ) : (
              <Select value={sourceWalletId} onValueChange={setSourceWalletId}>
                <SelectTrigger
                  className={cn(
                    'min-h-[52px] rounded-xl',
                    semanticTokens.motion.standard,
                    semanticTokens.focus.ring
                  )}
                  aria-label="Select source wallet"
                >
                  <SelectValue placeholder="Select wallet" />
                </SelectTrigger>
                <SelectContent>
                  {wallets?.map((wallet: BridgeWallet) => {
                    const totalBalance = wallet.balances.reduce(
                      (sum, b) => sum + parseFloat(b.balance || '0'),
                      0
                    );
                    return (
                      <SelectItem key={wallet.id} value={wallet.id}>
                        <div className="flex items-center justify-between w-full gap-4">
                          <div className="flex items-center gap-2">
                            <SFIcon icon={sfIcons.wallet} className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                            <span className={semanticTokens.typography.label}>{wallet.chain}</span>
                            <span className={cn(semanticTokens.typography.bodySmall, semanticTokens.fg.muted)}>
                              ({wallet.balances.map(b => b.currency).join(', ')})
                            </span>
                          </div>
                          <span className={cn(semanticTokens.typography.bodySmall, semanticTokens.fg.muted, "tabular-nums")}>
                            ${formatBalance(totalBalance)}
                          </span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Amount + Currency Row */}
          <div className="grid grid-cols-[1fr,auto] gap-3">
            <div className="space-y-2">
              <Label className={semanticTokens.typography.label}>Amount</Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                  $
                </span>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className={cn(
                    'min-h-[52px] rounded-xl pl-8 font-semibold tabular-nums',
                    semanticTokens.typography.bodyLarge,
                    semanticTokens.motion.standard,
                    semanticTokens.focus.ring
                  )}
                  aria-label="Transfer amount"
                />
              </div>
              {selectedWallet && (
                <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                  Available: ${formatBalance(availableBalance)} {currency}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className={semanticTokens.typography.label}>Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger
                  className={cn(
                    'min-h-[52px] w-[110px] rounded-xl',
                    semanticTokens.motion.standard,
                    semanticTokens.focus.ring
                  )}
                  aria-label="Select currency"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">
                    <div className="flex items-center gap-2">
                      <CurrencyIcon symbol="USD" size="sm" />
                      <span>USD</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="USDC">
                    <div className="flex items-center gap-2">
                      <CurrencyIcon symbol="USDC" size="sm" />
                      <span>USDC</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="EUR">
                    <div className="flex items-center gap-2">
                      <CurrencyIcon symbol="EUR" size="sm" />
                      <span>EUR</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Recipient Wallet ID */}
          <div className="space-y-2">
            <Label className={semanticTokens.typography.label}>Recipient Wallet ID</Label>
            <Input
              placeholder="Enter recipient's wallet ID"
              value={recipientId}
              onChange={(e) => setRecipientId(e.target.value)}
              className={cn(
                'min-h-[52px] rounded-xl',
                semanticTokens.typography.code,
                semanticTokens.motion.standard,
                semanticTokens.focus.ring
              )}
              aria-label="Recipient wallet ID"
            />
            <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
              Ask the recipient for their Supabank Wallet ID
            </p>
          </div>

          {/* Memo (Optional) */}
          <div className="space-y-2">
            <Label className={cn(semanticTokens.typography.label, 'flex items-center gap-2')}>
              Note
              <span className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                (optional)
              </span>
            </Label>
            <Input
              placeholder="What's this for?"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              maxLength={100}
              className={cn(
                'min-h-[52px] rounded-xl',
                semanticTokens.motion.standard,
                semanticTokens.focus.ring
              )}
              aria-label="Transfer memo"
            />
          </div>

        </div>

        {/* Footer - Sticky */}
        <SheetFooter className={cn(
          'flex-shrink-0 border-t pt-4',
          isMobile && 'pb-6' // Extra padding for iOS safe area
        )}>
          <Button
            className={cn(
              'w-full min-h-[52px] rounded-xl text-base font-semibold',
              semanticTokens.motion.standard,
              semanticTokens.focus.ring
            )}
            onClick={handleTransfer}
            disabled={!isValid || transferMutation.isPending}
            aria-label={`Send ${amount || '0'} ${currency}`}
          >
            {transferMutation.isPending ? (
              <>
                <InlineLoadingIndicator size="sm" className="mr-2" />
                Sending...
              </>
            ) : (
              <>
                <SFIcon icon={sfIcons.send} className="mr-2 h-5 w-5" aria-hidden="true" />
                Send {amount && parseFloat(amount) > 0 ? `$${parseFloat(amount).toFixed(2)}` : ''} {currency}
              </>
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

